import type { SelectItemProps } from '@radix-ui/react-select'
import type { DropdownProps, TriggerProps } from './Dropdown.types'
import * as Select from '@radix-ui/react-select'
import cn from 'classnames'
import React from 'react'
import { useUdsToken } from '../../utils'
import { BaseField, ValidationIcon } from '../fields/base-field/BaseField'
import { Icon } from '../icon'
import IconCell from '../icon-cell/IconCell'
import backdropStyles from '../overlays/backdrop.module.css'
import { ScrollArea } from '../scroll-area'
import { Typography } from '../typography'
import styles from './Dropdown.module.css'

export function Trigger({ ref, placeholder, label, className, onBlur, status, ...rest }: TriggerProps) {
  return (
    <BaseField label={label} className={cn(styles.baseFieldItem, className)} status={status} {...rest}>
      <Select.Trigger ref={ref} onBlur={onBlur} aria-label={label} className={styles.trigger} tabIndex={0}>
        <Select.Value placeholder={placeholder} />
        <div className={styles.validationIcon}>
          <ValidationIcon status={status ?? 'default'} />
        </div>
        <Select.Icon className={cn(styles.icon, 'SelectIcon')}>
          <Icon size={20} glyph="ChevronDown" />
        </Select.Icon>
      </Select.Trigger>
    </BaseField>
  )
}

export function Item({ ref, children, className, ...props }: React.PropsWithChildren<SelectItemProps> & { ref?: React.Ref<HTMLDivElement> }) {
  return (
    <Select.Item {...props} ref={ref} className={cn(styles.item, className)}>
      <Select.ItemIndicator className={styles.itemIndicator}>
        <IconCell glyph="CheckMarkSmall" size="sm" />
      </Select.ItemIndicator>
      <Select.ItemText>
        <Typography variant="body-m-regular-tight">{children}</Typography>
      </Select.ItemText>
    </Select.Item>
  )
}

export function Content({ children, className }: React.PropsWithChildren<{ className?: string }>) {
  const contentSideOffsetSpacing = Number(useUdsToken('primitives_space_12'))
  const contentSideOffset = -Number(useUdsToken('semantics_controls_m_min_height')) - contentSideOffsetSpacing

  return (
    <Select.Portal>
      <div className={styles.dropdownWrapper}>
        <Select.Content
          align="center"
          sideOffset={contentSideOffset}
          position="popper"
          avoidCollisions
          className={cn(styles.content, className)}
        >
          <Select.Viewport asChild>
            <ScrollArea.Root>
              <ScrollArea.Viewport>
                <Select.Group className={styles.selectGroup}>{children}</Select.Group>
              </ScrollArea.Viewport>
              <ScrollArea.Scrollbar orientation="vertical">
                <ScrollArea.Thumb />
              </ScrollArea.Scrollbar>
            </ScrollArea.Root>
          </Select.Viewport>
        </Select.Content>
      </div>
    </Select.Portal>
  )
}

export function Root({
  children,
  open,
  value,
  defaultValue,
  disabled,
  onValueChange,
  ...props
}: React.PropsWithChildren<DropdownProps>) {
  const [hideBackdrop, setHideBackdrop] = React.useState(true)

  const handleOpen = (open: boolean) => {
    setHideBackdrop(!open)
  }

  return (
    <Select.Root
      onOpenChange={handleOpen}
      open={open}
      value={value}
      defaultValue={defaultValue}
      disabled={disabled}
      onValueChange={onValueChange}
      {...props}
    >
      <Select.Portal>
        <div className={cn(backdropStyles.backdrop, styles.backdrop, { [styles.backdropHidden]: hideBackdrop })} />
      </Select.Portal>
      {children}
    </Select.Root>
  )
}

// This component is used to render an empty value item in the dropdown,
// due to Radix UI not allowing to set an empty value directly in the Select.Item component.
export function EmptyValueItem({
  className,
  children,
  value = null as unknown as string,
  ...props
}: Omit<React.ComponentProps<typeof Select.Item>, 'value'> & {
  value?: string
}) {
  return (
    <Item key="reset" value={value} className={className} {...props}>
      {children}
    </Item>
  )
}
